import { useState, useEffect, useCallback } from 'react';

// PWA Install Hook
export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) return false;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstallable(false);
      return true;
    }
    return false;
  }, [installPrompt]);

  return { isInstallable, isInstalled, install };
};

// Online/Offline Status Hook
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Push Notifications Hook
export const usePushNotifications = () => {
  const [permission, setPermission] = useState(Notification.permission);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.pushManager.getSubscription().then((sub) => {
          setSubscription(sub);
        });
      });
    }
  }, []);

  const requestPermission = useCallback(async () => {
    const result = await Notification.requestPermission();
    setPermission(result);
    return result === 'granted';
  }, []);

  const subscribe = useCallback(async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return null;
    }

    const registration = await navigator.serviceWorker.ready;
    
    // In production, use your VAPID public key
    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
      )
    });
    
    setSubscription(sub);
    return sub;
  }, [permission, requestPermission]);

  const showNotification = useCallback(async (title, options = {}) => {
    if (permission !== 'granted') return;
    
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [100, 50, 100],
      ...options
    });
  }, [permission]);

  return { permission, subscription, requestPermission, subscribe, showNotification };
};

// Vibration Hook
export const useVibration = () => {
  const vibrate = useCallback((pattern = [100]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  const vibrateSuccess = useCallback(() => vibrate([50, 30, 50]), [vibrate]);
  const vibrateError = useCallback(() => vibrate([100, 50, 100, 50, 100]), [vibrate]);
  const vibrateTap = useCallback(() => vibrate([10]), [vibrate]);

  return { vibrate, vibrateSuccess, vibrateError, vibrateTap };
};

// Geolocation Hook with Caching
export const useGeolocation = (options = {}) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        setLocation(loc);
        setLoading(false);
        
        // Cache location for offline use
        localStorage.setItem('hrms_last_location', JSON.stringify(loc));
      },
      (err) => {
        setError(err.message);
        setLoading(false);
        
        // Try to use cached location
        const cached = localStorage.getItem('hrms_last_location');
        if (cached) {
          setLocation({ ...JSON.parse(cached), cached: true });
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
        ...options
      }
    );
  }, [options]);

  const watchLocation = useCallback(() => {
    if (!navigator.geolocation) return null;

    return navigator.geolocation.watchPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      (err) => setError(err.message),
      { enableHighAccuracy: true, ...options }
    );
  }, [options]);

  return { location, error, loading, getLocation, watchLocation };
};

// Background Sync Hook
export const useBackgroundSync = () => {
  const queueAttendance = useCallback(async (data) => {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const registration = await navigator.serviceWorker.ready;
      
      // Store data for sync
      registration.active.postMessage({
        type: 'CACHE_ATTENDANCE',
        payload: data
      });
      
      // Register sync
      await registration.sync.register('attendance-sync');
      return true;
    }
    return false;
  }, []);

  const queueClaim = useCallback(async (data) => {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const registration = await navigator.serviceWorker.ready;
      
      registration.active.postMessage({
        type: 'CACHE_CLAIM',
        payload: data
      });
      
      await registration.sync.register('claim-sync');
      return true;
    }
    return false;
  }, []);

  return { queueAttendance, queueClaim };
};

// Camera Hook with better error handling
export const useCamera = () => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user');

  const startCamera = useCallback(async (videoElement) => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      });
      
      if (videoElement) {
        videoElement.srcObject = mediaStream;
        await videoElement.play();
      }
      
      setStream(mediaStream);
      setError(null);
      return mediaStream;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const switchCamera = useCallback(() => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, []);

  const capturePhoto = useCallback((videoElement, quality = 0.8) => {
    if (!videoElement) return null;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoElement, 0, 0);
    
    return canvas.toDataURL('image/jpeg', quality);
  }, []);

  return { stream, error, facingMode, startCamera, stopCamera, switchCamera, capturePhoto };
};

// Helper function for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default {
  usePWAInstall,
  useOnlineStatus,
  usePushNotifications,
  useVibration,
  useGeolocation,
  useBackgroundSync,
  useCamera
};
